import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { format, formatDistanceToNowStrict } from 'date-fns';
import {
  getPartnerRfq,
  respondToPartnerRfq,
  downloadPartnerTechpack,
} from '../../api/partnerPortal';

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return format(new Date(value), 'MMM d, yyyy • h:mm a');
  } catch {
    return value;
  }
};

const formatRelative = (value) => {
  if (!value) return '—';
  try {
    return formatDistanceToNowStrict(new Date(value), { addSuffix: true });
  } catch {
    return '—';
  }
};

const defaultResponseState = {
  commit: false,
  unitCost: '',
  currency: 'USD',
  minimumUnits: '',
  maximumUnits: '',
  leadTimeDays: '',
  shippingTerms: '',
  fulfillmentPlan: '',
  paymentTerms: '',
  notes: '',
};

const PartnerRFQDetailPage = () => {
  const { rfqId } = useParams();
  const navigate = useNavigate();
  const [mode, setMode] = useState('respond');
  const [response, setResponse] = useState(defaultResponseState);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    ['partner-rfq', rfqId],
    () => getPartnerRfq(rfqId),
    {
      enabled: !!rfqId,
    }
  );

  const rfq = data?.rfq;
  const assignment = rfq?.assignment;

  const respondMutation = useMutation(
    (payload) => respondToPartnerRfq(rfqId, payload),
    {
      onSuccess: (result) => {
        setSuccessMessage('Response submitted successfully.');
        setErrorMessage('');
        if (result?.rfq) {
          refetch();
        }
      },
      onError: (err) => {
        setSuccessMessage('');
        setErrorMessage(
          err?.response?.data?.error ||
            err?.message ||
            'Failed to submit response. Please try again.'
        );
      },
    }
  );

  const handleChange = (field) => (event) => {
    setResponse((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmitResponse = (commit) => {
    respondMutation.mutate({
      commit,
      terms: {
        unitCost: response.unitCost ? Number(response.unitCost) : undefined,
        currency: response.currency || 'USD',
        minimumUnits: response.minimumUnits ? Number(response.minimumUnits) : undefined,
        maximumUnits: response.maximumUnits ? Number(response.maximumUnits) : undefined,
        leadTimeDays: response.leadTimeDays ? Number(response.leadTimeDays) : undefined,
        shippingTerms: response.shippingTerms,
        fulfillmentPlan: response.fulfillmentPlan,
        paymentTerms: response.paymentTerms,
      },
      notes: response.notes,
    });
  };

  const handleDecline = () => {
    respondMutation.mutate({
      action: 'decline',
      notes: response.notes || 'Declined by partner',
    });
  };

  const handleDownloadTechpack = async () => {
    try {
      const response = await downloadPartnerTechpack(rfqId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      const filename = response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || 'techpack.yaml';
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      setErrorMessage(
        downloadError?.response?.data?.error ||
          downloadError?.message ||
          'Unable to download tech pack.'
      );
    }
  };

  const productionRequirements = useMemo(
    () => rfq?.requirements?.production || {},
    [rfq]
  );

  const shippingRequirements = useMemo(
    () => rfq?.requirements?.shipping || {},
    [rfq]
  );

  const fulfillmentRequirements = useMemo(
    () => rfq?.requirements?.fulfillment || {},
    [rfq]
  );

  const qualityRequirements = useMemo(
    () => rfq?.requirements?.quality || {},
    [rfq]
  );

  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {rfq?.title || 'RFQ Details'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Request for Quote • {rfq?.code || rfqId}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            {rfq?.brand?.name && (
              <Chip label={rfq.brand.name} size="small" variant="outlined" />
            )}
            {assignment?.status && (
              <Chip label={assignment.status} size="small" color="primary" />
            )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Back to RFQs
          </Button>
          <Button
            variant="contained"
            onClick={handleDownloadTechpack}
            disabled={respondMutation.isLoading}
          >
            Download Tech Pack
          </Button>
        </Stack>
      </Stack>

      {isFetching && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <CircularProgress size={18} />
          <Typography variant="caption">Refreshing…</Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error">
          {error?.response?.data?.error || error?.message || 'Failed to load RFQ.'}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : rfq ? (
        <>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Deadlines
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Response due
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(rfq.deadlines?.responseDue)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatRelative(rfq.deadlines?.responseDue)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Decision date
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(rfq.deadlines?.decisionDate)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Drop date
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(rfq.deadlines?.dropDate)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Production Requirements
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Target units
                </Typography>
                <Typography variant="body1">
                  {productionRequirements.targetUnits ?? '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Minimum units
                </Typography>
                <Typography variant="body1">
                  {productionRequirements.minimumUnits ?? '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Maximum units
                </Typography>
                <Typography variant="body1">
                  {productionRequirements.maximumUnits ?? '—'}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  Materials
                </Typography>
                <Typography variant="body2">
                  {(productionRequirements.materials || []).join(', ') || '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  Processes
                </Typography>
                <Typography variant="body2">
                  {(productionRequirements.processes || []).join(', ') || '—'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Size curve
                </Typography>
                <Typography variant="body2">
                  {(productionRequirements.sizeCurve || []).join(', ') || '—'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  height: '100%',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Shipping & Fulfillment
                </Typography>
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Incoterms / Shipping Terms
                    </Typography>
                    <Typography variant="body2">
                      {shippingRequirements.incoterms || '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Destination
                    </Typography>
                    <Typography variant="body2">
                      {shippingRequirements.destination || '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Packaging
                    </Typography>
                    <Typography variant="body2">
                      {shippingRequirements.packaging || '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Fulfillment strategy
                    </Typography>
                    <Typography variant="body2">
                      {fulfillmentRequirements.strategy || '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Fulfillment notes
                    </Typography>
                    <Typography variant="body2">
                      {fulfillmentRequirements.notes || '—'}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  height: '100%',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Quality & Compliance
                </Typography>
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Quality standards
                    </Typography>
                    <Typography variant="body2">
                      {(qualityRequirements.standards || []).join(', ') || '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body2">
                      {qualityRequirements.notes || '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Sustainability
                    </Typography>
                    <Typography variant="body2">
                      {(productionRequirements.sustainability || []).join(', ') || '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Compliance
                    </Typography>
                    <Typography variant="body2">
                      {(productionRequirements.compliance || []).join(', ') || '—'}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Tech Pack & Assets
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {rfq.techpack?.summary && (
                <Box>
                  <Typography variant="subtitle2">Quick Summary</Typography>
                  <pre
                    style={{
                      marginTop: 8,
                      background: '#1c1c1c',
                      color: '#f0f0f0',
                      fontSize: 12,
                      borderRadius: 8,
                      padding: 16,
                      overflow: 'auto',
                    }}
                  >
                    {JSON.stringify(rfq.techpack.summary, null, 2)}
                  </pre>
                </Box>
              )}
              <Box>
                <Typography variant="subtitle2">Attachments</Typography>
                <Stack spacing={1} mt={1}>
                  {(rfq.techpack?.attachments || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No attachments provided.
                    </Typography>
                  ) : (
                    rfq.techpack.attachments.map((file) => (
                      <Link
                        key={file.url}
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {file.name || file.url}
                      </Link>
                    ))
                  )}
                </Stack>
              </Box>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              spacing={2}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Respond / Negotiate
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button variant={mode === 'respond' ? 'contained' : 'outlined'} onClick={() => setMode('respond')}>
                  Submit Response
                </Button>
                <Button variant={mode === 'notes' ? 'contained' : 'outlined'} onClick={() => setMode('notes')}>
                  Notes
                </Button>
              </Stack>
            </Stack>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Unit cost"
                  value={response.unitCost}
                  onChange={handleChange('unitCost')}
                  fullWidth
                  InputProps={{ inputMode: 'decimal' }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  label="Currency"
                  value={response.currency}
                  onChange={handleChange('currency')}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Minimum units"
                  value={response.minimumUnits}
                  onChange={handleChange('minimumUnits')}
                  fullWidth
                  InputProps={{ inputMode: 'numeric' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Maximum units"
                  value={response.maximumUnits}
                  onChange={handleChange('maximumUnits')}
                  fullWidth
                  InputProps={{ inputMode: 'numeric' }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Lead time (days)"
                  value={response.leadTimeDays}
                  onChange={handleChange('leadTimeDays')}
                  fullWidth
                  InputProps={{ inputMode: 'numeric' }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Shipping terms"
                  value={response.shippingTerms}
                  onChange={handleChange('shippingTerms')}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Fulfillment plan"
                  value={response.fulfillmentPlan}
                  onChange={handleChange('fulfillmentPlan')}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Payment terms"
                  value={response.paymentTerms}
                  onChange={handleChange('paymentTerms')}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notes / considerations"
                  value={response.notes}
                  onChange={handleChange('notes')}
                  multiline
                  minRows={3}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              justifyContent="flex-end"
              sx={{ mt: 3 }}
            >
              <Button
                variant="outlined"
                color="error"
                onClick={handleDecline}
                disabled={respondMutation.isLoading}
              >
                Decline RFQ
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleSubmitResponse(false)}
                disabled={respondMutation.isLoading}
              >
                Propose Counter Terms
              </Button>
              <Button
                variant="contained"
                onClick={() => handleSubmitResponse(true)}
                disabled={respondMutation.isLoading}
              >
                Commit to RFQ
              </Button>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Response History
            </Typography>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {(assignment?.history || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No response history yet. Submit your initial response above.
                </Typography>
              ) : (
                (assignment.history || [])
                  .slice()
                  .reverse()
                  .map((entry, index) => (
                    <Paper
                      key={`${entry.submittedAt}_${index}`}
                      variant="outlined"
                      sx={{ p: 2, borderRadius: 2 }}
                    >
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="subtitle2">
                          {entry.committed ? 'Committed terms' : entry.status === 'declined' ? 'Declined' : 'Negotiation update'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(entry.submittedAt)}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {entry.notes || 'No notes provided.'}
                      </Typography>
                      {entry.terms && (
                        <Grid container spacing={1} sx={{ mt: 1 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" color="text.secondary">
                              Unit cost
                            </Typography>
                            <Typography variant="body2">
                              {entry.terms.unitCost
                                ? `${entry.terms.currency || 'USD'} ${entry.terms.unitCost}`
                                : '—'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" color="text.secondary">
                              Lead time
                            </Typography>
                            <Typography variant="body2">
                              {entry.terms.leadTimeDays
                                ? `${entry.terms.leadTimeDays} days`
                                : '—'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" color="text.secondary">
                              Capacity
                            </Typography>
                            <Typography variant="body2">
                              {entry.terms.minimumUnits || '—'} / {entry.terms.maximumUnits || '—'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" color="text.secondary">
                              Shipping
                            </Typography>
                            <Typography variant="body2">
                              {entry.terms.shippingTerms || '—'}
                            </Typography>
                          </Grid>
                        </Grid>
                      )}
                      {(entry.attachments || []).length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Attachments
                          </Typography>
                          <Stack spacing={1}>
                            {entry.attachments.map((file) => (
                              <Link
                                key={file.url}
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {file.name || file.url}
                              </Link>
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Paper>
                  ))
              )}
            </Stack>
          </Paper>
        </>
      ) : null}
    </Box>
  );
};

export default PartnerRFQDetailPage;
